import type { ReactNode } from "react";

import { Box, Stack } from "@mantine/core";

import styles from "./NarrativeContainer.module.css";

export interface NarrativeContainerProps {
    /** Pinned to the top — the NarrativeHeader (product card). */
    header?: ReactNode;
    /** Scrollable content between header and footer — the NarrativePanel or a step's own content. */
    children: ReactNode;
    /** A bottom-sheet overlay anchored to the container (e.g. the quick-rebuttal panel). The overlay
     *  positions itself (absolute); the container just provides the relative anchor + height. */
    overlay?: ReactNode;
    /** Pinned to the bottom — the NarrativeFooter. The container owns the one true footer. */
    footer?: ReactNode;
}

/**
 * Layout-agnostic shell for the Long Form AI sale surface. Fills its host, pins the header and
 * footer, and scrolls the content between — so it slots in UNCHANGED in both shells:
 *
 * - redesigned: below the tabs + "Back to offers" in the Offers pane
 * - legacy: inside the floating panel body, below the panel's own "Home Device Protect" title bar
 *   (two headers there is accepted — Mason 2026-08-25)
 *
 * Because the container owns the footer, the existing sale footer (legacy `SalesFooter` and the
 * redesigned footer bar) is suppressed by the LFA override, so exactly one footer renders in either
 * shell. Integration/override wiring is a later task; the component is built to that contract.
 */
export const NarrativeContainer = ({ header, children, overlay, footer }: NarrativeContainerProps) => (
    <Stack gap={0} className={styles.container}>
        {header && (
            <Box flex="0 0 auto" px={16} pt={16} pb={12}>
                {header}
            </Box>
        )}
        <Box flex="1 1 auto" mih={0} className={styles.main}>
            <Box className={styles.content}>{children}</Box>
            {/* Bottom-sheet overlay (e.g. quick rebuttal) — anchored to this region, so it sits ABOVE
                the footer, which stays visible below it. */}
            {overlay}
        </Box>
        {footer && <Box flex="0 0 auto">{footer}</Box>}
    </Stack>
);
